import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { MasterModel } from '../../../../master/models/master.model';
import { MasterService } from '../../../../master/services/master.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SubventionService } from '../../../../../modules/invoice/subvention/subvention.service';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';
import { ConboxDialogComponent } from '../../../../../shared/components/add-file/dialog-conbox.component';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { ActualSelected, LocalDB } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';

@Component({
  selector: 'app-grant-form',
  templateUrl: './grant-form.component.html',
  styleUrls: ['./grant-form.component.css'],
  standalone: false
})
export class GrantFormV2Component implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  uid: string | null = null;

  isLoading = true;
  isSaving = false;
  loadingFile = false;
  isProjectEnable = false;

  subventionTypes: MasterModel[] = [];
  contacts: MasterModel[] = [];
  bankAccounts: MasterModel[] = [];
  accounts: MasterModel[] = [];
  investments: MasterModel[] = [];
  projects: MasterModel[] = [];

  fileUid: string | null = null;
  isImage = false;
  isPdf = false;
  imageUrl: string | null = null;
  pdfUrl: SafeResourceUrl | null = null;

  private objectUrl: string | null = null;
  private expenseAccount: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private masterService: MasterService,
    private subventionService: SubventionService,
    private activityService: ActivityService,
    private addFileService: AddFileService,
    private loading: LoadingDialogService,
    private notification: NotificationService,
  ) {
    this.form = this.fb.group({
      subventionTypeUid: [null, Validators.required],
      provider: [null, Validators.required],
      date: [new Date(), Validators.required],
      elementDescription: ['', Validators.required],
      projectUid: [null],
      totalWithoutTax: [null, [Validators.required, Validators.min(0.01)]],
      paided: [new Date(), Validators.required],
      bankAccountUid: [null, Validators.required],
      entranceAccount: [null, Validators.required],
      investmentUid: [null],
    });
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.uid = params['uid'] || null;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.revokePreview();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasFile(): boolean {
    return !!this.fileUid && this.fileUid !== consts.EMPTY_GUID;
  }

  get isExplotacion(): boolean {
    const type = this.subventionTypes.find(t => this.sameId(t.id, this.form.get('subventionTypeUid').value));
    return !!type && type.display === consts.SUBVENTION_ACCOUNT_TYPE_EXPLOTACION;
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    try {
      const [lookups, activity, grant] = await Promise.all([
        firstValueFrom(forkJoin({
          types: this.masterService.getSubventionTypes(),
          contacts: this.masterService.getContacts(),
          bankAccounts: this.masterService.getBankAccounts(),
          accounts: this.masterService.getAccountingModels(),
          investments: this.masterService.getInvestments(),
        })),
        this.activityService.loadData(ActualSelected.activitySelected),
        this.uid ? this.subventionService.loadData(this.uid) : Promise.resolve(null),
      ]);

      this.subventionTypes = lookups.types || [];
      this.contacts = lookups.contacts || [];
      this.bankAccounts = (lookups.bankAccounts || []).map(account => ({
        ...account,
        display: account.metadata && account.metadata.bank ? `${account.metadata.bank} — ${account.display}` : account.display,
      }));
      this.accounts = (lookups.accounts || []).filter(account => String(this.accountNumber(account)).startsWith('7'));
      this.investments = lookups.investments || [];

      this.isProjectEnable = !!(activity && activity.isProject);
      if (this.isProjectEnable) {
        this.projects = await firstValueFrom(this.masterService.getProjects()) || [];
      }

      if (grant) {
        this.applyGrant(grant);
      } else {
        this.applyTypeRules(false);
        const requestedProjectUid = this.route.snapshot.queryParamMap.get('projectUid');
        if (requestedProjectUid && this.isProjectEnable) {
          const project = this.projects.find(p => this.sameId(p.id, requestedProjectUid));
          if (project) this.form.patchValue({ projectUid: project });
        }
      }

      this.isLoading = false;
    } catch {
      this.isLoading = false;
      this.notification.errorBottom(this.uid ? 'No se pudo cargar la subvención.' : 'Error cargando datos del formulario.');
      if (this.uid) this.router.navigate(['invoice/subvention']);
    }
  }

  private applyGrant(grant: any): void {
    const type = this.subventionTypes.find(t => this.sameId(t.id, grant.subventionTypeUid));
    const bank = this.bankAccounts.find(b => this.sameId(b.id, grant.bankAccountUid));
    const account = this.accounts.find(a => this.accountNumber(a) === grant.entranceAccount);
    const investment = this.investments.find(i => this.sameId(i.id, grant.investmentUid));
    const project = this.projects.find(p => this.sameId(p.id, grant.projectUid));

    this.expenseAccount = grant.expenseAccount || null;

    this.form.patchValue({
      subventionTypeUid: type ? type.id : null,
      provider: grant.provider ? { id: grant.provider, display: grant.provider } : null,
      date: grant.date ? new Date(grant.date) : null,
      elementDescription: grant.elementDescription || '',
      projectUid: project || null,
      totalWithoutTax: grant.totalWithoutTax,
      paided: grant.paided ? new Date(grant.paided) : null,
      bankAccountUid: bank || null,
      entranceAccount: account || (grant.entranceAccount ? { id: grant.entranceAccount, display: grant.entranceAccount } : null),
      investmentUid: investment || null,
    }, { emitEvent: false });

    this.applyTypeRules(false);

    this.fileUid = grant.fileUid && grant.fileUid !== consts.EMPTY_GUID ? grant.fileUid : null;
    this.isImage = !!grant.image;
    this.isPdf = !!grant.pdf;
    if (this.hasFile && (this.isImage || this.isPdf)) {
      this.loadingFile = true;
      this.addFileService.getImageFromFiles(this.fileUid, consts.SUBVENTION_ENTITY)
        .then(blob => this.showPreview(blob))
        .catch(() => this.notification.warningBottom('No se pudo cargar la vista previa del documento.'))
        .finally(() => this.loadingFile = false);
    }
  }

  onTypeChange(): void {
    this.applyTypeRules(true);
  }

  private applyTypeRules(setDefaultAccount: boolean): void {
    const investmentCtrl = this.form.get('investmentUid');
    const explotacion = this.isExplotacion;

    if (explotacion) {
      investmentCtrl.clearValidators();
      if (setDefaultAccount || !investmentCtrl.value) {
        investmentCtrl.setValue(null, { emitEvent: false });
        investmentCtrl.disable({ emitEvent: false });
      }
    } else {
      investmentCtrl.enable({ emitEvent: false });
      investmentCtrl.setValidators(Validators.required);
    }
    investmentCtrl.updateValueAndValidity({ emitEvent: false });

    if (setDefaultAccount) {
      const prefix = explotacion ? consts.SUBVENTION_ACCOUNT_EXPLOTACION : consts.SUBVENTION_ACCOUNT_CAPITAL;
      const account = this.accounts.find(a => String(this.accountNumber(a)).startsWith(prefix));
      if (account) this.form.get('entranceAccount').setValue(account);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    const name = file.name.toLowerCase();
    const pdf = file.type === 'application/pdf' || name.endsWith('.pdf');
    const image = /\.(png|jpe?g)$/.test(name) || file.type === 'image/png' || file.type === 'image/jpeg';
    if (!pdf && !image) {
      this.notification.warningBottom('Solo se admiten archivos PDF, PNG o JPG.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    this.loadingFile = true;
    this.addFileService.attachFile(formData, consts.SUBVENTION_ENTITY)
      .then(response => {
        if (!response || response.error) {
          this.notification.errorBottom(response && response.message ? response.message : 'No se pudo subir el archivo.');
          return;
        }
        LocalDB.RemoveForFiles();
        this.fileUid = response.uid;
        this.isPdf = pdf;
        this.isImage = !pdf;
        this.showPreview(file);
      })
      .catch(error => this.notification.errorBottom(error && error.error && error.error.message ? error.error.message : 'No se pudo subir el archivo.'))
      .finally(() => this.loadingFile = false);
  }

  pickFromFiles(): void {
    this.dialog.open(ConboxDialogComponent, {
      width: '80%',
      height: '80%',
      maxWidth: '96vw',
      data: { originEntity: consts.SUBVENTION_ENTITY, filePath: consts.SUBVENTION_FILE_PATH }
    }).afterClosed().subscribe(result => {
      if (!result || !result.uid) return;

      const pdf = result.type === consts.TYPE_FILE_PDF;
      const image = result.type === consts.TYPE_FILE_IMAGE;
      if (!pdf && !image) return;

      this.loadingFile = true;
      const request = pdf
        ? this.addFileService.getImageFromFilesPDF(result.uid, consts.SUBVENTION_ENTITY)
        : this.addFileService.getImageFromFiles(result.uid, consts.SUBVENTION_ENTITY);

      request
        .then(blob => {
          this.fileUid = result.uid;
          this.isPdf = pdf;
          this.isImage = image;
          this.showPreview(blob);
        })
        .catch(() => this.notification.errorBottom('No se pudo cargar el documento seleccionado.'))
        .finally(() => this.loadingFile = false);
    });
  }

  removeFile(): void {
    this.fileUid = null;
    this.isImage = false;
    this.isPdf = false;
    this.revokePreview();
  }

  private showPreview(blob: Blob): void {
    this.revokePreview();
    if (!blob) return;
    this.objectUrl = URL.createObjectURL(blob);
    if (this.isPdf) {
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    } else {
      this.imageUrl = this.objectUrl;
    }
  }

  private revokePreview(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.imageUrl = null;
    this.pdfUrl = null;
  }

  onCancel(): void {
    this.router.navigate(['invoice/subvention']);
  }

  onSave(): void {
    if (this.isSaving || this.loadingFile) return;

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.showValidationErrors();
      return;
    }

    const value = this.form.getRawValue();
    const provider = value.provider;
    const dto: any = {
      activityUid: ActualSelected.activitySelected,
      subventionTypeUid: value.subventionTypeUid,
      provider: typeof provider === 'string' ? provider.trim() : (provider.display || provider.id || '').trim(),
      elementDescription: value.elementDescription,
      date: moment(value.date).format('YYYY-MM-DD'),
      paided: moment(value.paided).format('YYYY-MM-DD'),
      totalWithoutTax: Number(value.totalWithoutTax),
      bankAccountUid: value.bankAccountUid.id,
      entranceAccount: this.accountNumber(value.entranceAccount),
      expenseAccount: this.expenseAccount,
      investmentUid: value.investmentUid && value.investmentUid.id ? value.investmentUid.id : consts.EMPTY_GUID,
      projectUid: value.projectUid && value.projectUid.id ? value.projectUid.id : null,
      fileUid: this.hasFile ? this.fileUid : consts.EMPTY_GUID,
      image: this.hasFile && this.isImage,
      pdf: this.hasFile && this.isPdf,
    };
    if (this.uid) dto.uid = this.uid;

    const request = this.uid
      ? this.subventionService.editData(this.uid, dto)
      : this.subventionService.addData(dto);

    this.isSaving = true;
    this.loading.run(this.uid ? 'Guardando subvención…' : 'Creando subvención…', request)
      .then(() => {
        this.notification.okBottom('Subvención guardada correctamente');
        this.router.navigate(['invoice/subvention']);
      })
      .catch(error => {
        this.notification.errorBottom(error && error.error && error.error.message ? error.error.message : 'Error guardando la subvención.');
      })
      .finally(() => this.isSaving = false);
  }

  private showValidationErrors(): void {
    const labels: { [key: string]: string } = {
      subventionTypeUid: 'Selecciona el tipo de subvención.',
      provider: 'Indica la administración u organismo.',
      date: 'Indica la fecha de concesión.',
      elementDescription: 'Describe la subvención.',
      totalWithoutTax: 'Indica el total recibido (mayor que 0).',
      paided: 'Indica la fecha de cobro.',
      bankAccountUid: 'Selecciona la cuenta bancaria.',
      entranceAccount: 'Selecciona la cuenta contable de ingreso.',
      investmentUid: 'Selecciona la inversión.',
    };

    const messages = Object.keys(labels)
      .filter(key => this.form.get(key).invalid)
      .map(key => '• ' + labels[key]);

    this.notification.errorBottom('Revisa los campos marcados en rojo:\n' + messages.join('\n'));

    setTimeout(() => {
      const firstInvalid = document.querySelector('app-grant-form .mat-form-field-invalid, app-grant-form .mat-mdc-form-field-invalid');
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private sameId(a: any, b: any): boolean {
    return a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();
  }

  private accountNumber(account: any): string {
    if (!account) return '';
    if (account.metadata && account.metadata.number) return String(account.metadata.number);
    const display = String(account.display || account.id || '');
    return display.split(' - ')[0].trim();
  }
}
